import React from "react";
import { type TableColumn, getColumnClassName } from "./common";
import { type OrderableTableFooter } from "./OrderableTable";
import TableWithHeaderBase from "./TableWithHeaderBase";
import classNames from "classnames";
import { PhosphorPencilButton, PhosphorLinkBreakButton, PhosphorTrashButton } from "components/Atoms/PhosphorIconButton";

export interface TableProps<T> {
  variant?: "regular" | "cards",
  items: T[]
  isLoading: boolean
  getKey: (item: T) => string
  columns: Array<TableColumn<T>>
  title?: string
  pagination?: React.ReactNode
  hideBottomPagination?: boolean
  hideNoDataAvailableDiv?: boolean
  exportButton?: React.ReactNode
  footers?: OrderableTableFooter[]
  onEdit?: (item: T) => void
  canEdit?: (item: T) => boolean
  onDelete?: (item: T) => void
  canDelete?: (item: T) => boolean
  deleteIcon?: "trash" | "linkBreak"
  hideHeader?: boolean
  isSelected?: (item: T) => boolean
  selectionEnabled?: boolean
  selectionCount?: number
  selectedOnThisPage?: number
  showingOnlyMySelection?: boolean
  supportArticleId?: number
  toggleAll?: () => void
  layoutButtons?: React.ReactNode
}

const Table = <T extends any>(props: TableProps<T>) => {
  const {
    variant,
    items,
    isLoading,
    getKey,
    columns,
    title,
    pagination,
    hideBottomPagination,
    hideNoDataAvailableDiv,
    exportButton,
    footers = [],
    onDelete,
    canDelete = () => true,
    onEdit,
    canEdit = () => true,
    isSelected = () => false,
    selectionEnabled,
    selectionCount,
    selectedOnThisPage,
    showingOnlyMySelection,
    toggleAll,
    layoutButtons
  } = props;

  const visibleColumns = columns.filter(column => {
    if (typeof column.hidden === "function") {
      return !column.hidden(items);
    }

    return !column.hidden;
  });
  const visibleFooters = footers.map(footer => ({
    ...footer,
    entries: footer.entries.filter((_, index) => !columns[index].hidden)
  }));

  const displayEdit = onEdit != null;
  const canEditItem = (item: T) => canEdit(item) && displayEdit;
  const displayDelete = onDelete != null;
  const canDeleteItem = (item: T) => canDelete(item) && displayDelete;

  const gridTemplateColumns = [
    ...visibleColumns.map(column => column.size ?? "auto"),
    ...[displayDelete ? "30px" : "", displayEdit ? "30px" : ""]
  ];
  const gridTemplateRows = [
    ...[!props.hideHeader ? "minmax(30px, auto)" : ""],
    "auto"
  ];

  return <TableWithHeaderBase
    title={title}
    pagination={pagination}
    hideBottomPagination={hideBottomPagination}
    hideNoDataAvailableDiv={hideNoDataAvailableDiv}
    exportButton={exportButton}
    isLoading={isLoading}
    items={items}
    selectionEnabled={selectionEnabled}
    selectionCount={selectionCount}
    selectedOnThisPage={selectedOnThisPage}
    showingOnlyMySelection={showingOnlyMySelection}
    toggleAll={toggleAll}
    layoutButtons={layoutButtons}
  >
    <div style={{
      display: "grid",
      gridTemplateColumns: gridTemplateColumns.join(" "),
      gridTemplateRows: gridTemplateRows.join(" ")
    }}>
      {!props.hideHeader &&
        <div className="grid-table-header">
          {visibleColumns.map(column => (
            <div className={getColumnClassName(column)} key={column.key}>{column.header}</div>
          ))}
          {displayDelete && <div style={{ width: 30 }} />}
          {displayEdit && <div style={{ width: 30 }} />}
        </div>}

      {items.map((item, idx) => (
        <div className={classNames("grid-table-row", {
          selected: isSelected(item),
          "grid-table-row-cards": variant === "cards",
          "grid-table-last-row": idx === items.length - 1
        })} key={getKey(item)}>
          {visibleColumns.map(column => (
            <div style={column.style} className={getColumnClassName(column, item)} key={column.key}>{column.render(item)}</div>
          ))}
          {displayDelete && (<div style={{ width: 30 }} className="s1_button_cell">
            {(canDeleteItem(item) && props.deleteIcon === "linkBreak") && <PhosphorLinkBreakButton
              layout="in-square"
              onClick={() => { onDelete(item); }} />}
            {(canDeleteItem(item) && props.deleteIcon !== "linkBreak") && <PhosphorTrashButton
              layout="in-square"
              onClick={() => { onDelete(item); }} />}
          </div>)}
          {displayEdit && (<div style={{ width: 30 }} className="s1_button_cell">
            {canEditItem(item) && <PhosphorPencilButton
              layout="in-square"
              onClick={() => { onEdit(item); }}
            />}
          </div>)}
        </div>
      ))}

      {visibleFooters?.map((footer) => (
        <div className="grid-table-footer" key={footer.key}>
          {footer.entries.map((entry, index) => (
            <div className={classNames(footer.className, getColumnClassName(columns[index]))} key={columns[index].key}>{entry}</div>
          ))}
          {displayDelete && <div className={footer.className} style={{ width: 30 }} />}
          {displayEdit && <div className={footer.className} style={{ width: 30 }} />}
        </div>
      ))}
    </div>
  </TableWithHeaderBase>;
};

export default Table;